import { useState, useMemo } from "react";
import type { SimilarTx } from "../../lib/api";

export function formatSmallNumber(n: number): string {
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 0.01) return n.toFixed(4);
  if (abs >= 1e-6) return n.toFixed(8);
  return n.toExponential(2);
}

export function similarTxToVector(tx: SimilarTx): (number | string)[] {
  if (tx.featureVector) return tx.featureVector;
  return [
    tx.numNoteHashes,
    tx.numNullifiers,
    tx.numL2ToL1Msgs,
    tx.numPrivateLogs,
    tx.numContractClassLogs,
    tx.numPublicLogs ?? 0,
    tx.gasLimitDa ?? 0,
    tx.gasLimitL2 ?? 0,
    tx.maxFeePerDaGas ?? 0,
    tx.maxFeePerL2Gas ?? 0,
    tx.numSetupCalls,
    tx.numAppCalls,
    tx.hasTeardown ? 1 : 0,
    tx.totalPublicCalldataSize,
    tx.expirationTimestamp ?? 0,
    tx.feePayer,
  ];
}

export function formatDelta(seconds: number): string {
  if (seconds < 0) return `-${formatDelta(-seconds)}`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

export type SortDir = "asc" | "desc";

export function useSortableTable<T>(
  data: T[],
  defaultKey: keyof T & string,
  defaultDir: SortDir = "asc",
) {
  const [sortKey, setSortKey] = useState<keyof T & string>(defaultKey);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key: keyof T & string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const indicator = (key: keyof T & string) =>
    key === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return { sorted, sortKey, sortDir, toggleSort, indicator };
}

export const FEATURE_LABELS = [
  "Note Hashes",
  "Nullifiers",
  "L2 to L1 Messages",
  "Private Logs",
  "Contract Class Logs",
  "Public Logs",
  "DA Mana Limit",
  "L2 Mana Limit",
  "Max Fee Per DA Mana",
  "Max Fee Per L2 Mana",
  "Setup Calls",
  "App Calls",
  "Has Teardown",
  "Total Public Calldata Size",
  "Expiration Delta",
  "Fee Payer",
];
